/**
 * The extensions every Dossier editor uses, in one place (#277): the page
 * editors and the nested editor inside an excerpt embed must share one schema,
 * or an excerpt holding a table or an image could not be shown where it was
 * sent.
 */
import type { Extensions, AnyExtension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { DossierLink } from './LinkExtension';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { Mention } from '@tiptap/extension-mention';
import { common, createLowlight } from 'lowlight';
import Details, { DetailsContent, DetailsSummary } from '@tiptap/extension-details';
import type { SuggestionOptions } from '@tiptap/suggestion';
import { formattingExtensions } from './FormattingExtensions';
import { ResizableImage } from './ResizableImageExtension';
import { tagSuggestion } from './TagExtension';
import { entityLinkSuggestion } from './EntityLinkExtension';
import { TaskReference } from './TaskReferenceExtension';
import { NoteReference } from './NoteReferenceExtension';
import { EntityLinkNode } from './EntityLinkNode';
import { ImageUpload } from './ImageUploadExtension';
import { AttachmentUpload } from './AttachmentUploadExtension';
import { AttachmentChipNode } from './AttachmentChipNode';
import { AutoSection } from './AutoSectionExtension';
import { Indent } from './IndentExtension';
import { ExcerptMark } from './ExcerptMark';
import { ExcerptEmbed } from './ExcerptEmbed';
import { FlagExtension, type FlagExtensionOptions } from './FlagExtension';
import { TableMath } from './TableMathExtension';
import { NamedList, NamedListItem, type NamedListOptions, type NamedListItemOptions } from './lists/NamedListNodes';
import { LineIdExtension } from './lists/LineIdExtension';

const lowlight = createLowlight(common);

/**
 * Extended Mention node that adds `color` and `followUpTaskId` attributes.
 * The stock Mention extension only has id, label, and mentionSuggestionChar.
 */
const DossierMention = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      color: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-color'),
        renderHTML: (attributes) => {
          if (!attributes.color) return {};
          return { 'data-color': attributes.color };
        },
      },
      followUpTaskId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-follow-up-task-id'),
        renderHTML: (attributes) => {
          if (!attributes.followUpTaskId) return {};
          return { 'data-follow-up-task-id': attributes.followUpTaskId };
        },
      },
    };
  },
});

export interface DossierExtensionOptions {
  placeholder: string;
  handleImageError: (message: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mentionSuggestion: Omit<SuggestionOptions<any, any>, 'editor'>;
  onTaskStatusChange?: (taskId: string, newStatus: string) => void;
  scratchpadId?: string;
  followUpExtension: AnyExtension;
  /** Flags (#316): on only for a document that is a flag source; the attribute parses everywhere. */
  flags?: Partial<FlagExtensionOptions>;
  /** Named lists (#337): the picker and the actions the host page provides. */
  lists?: NamedListOptions & NamedListItemOptions;
}

export function dossierExtensions({
  placeholder,
  handleImageError,
  mentionSuggestion,
  onTaskStatusChange,
  scratchpadId,
  followUpExtension,
  flags,
  lists,
}: DossierExtensionOptions): Extensions {
  return [
    StarterKit.configure({
      // Disable the built-in codeBlock so we use lowlight version
      codeBlock: false,
      // StarterKit v3 bundles Link; ours (DossierLink, below) replaces it so
      // the schema has exactly one, non-inclusive, link mark (#96).
      link: false,
    }),
    Placeholder.configure({
      placeholder,
      emptyEditorClass: 'is-editor-empty',
    }),
    TaskList,
    TaskItem.configure({
      nested: true,
    }),
    Table.configure({
      resizable: true,
    }),
    TableRow,
    TableCell,
    TableHeader,
    // Formulas and totals in table cells (#338).
    TableMath,
    // Named lists (#337): lists, their items, and the line ids filed items point at.
    NamedList.configure({
      onOpenListPicker: lists?.onOpenListPicker,
      onTurnOpenItemsIntoTasks: lists?.onTurnOpenItemsIntoTasks,
      onBringForward: lists?.onBringForward,
      mirror: lists?.mirror,
    }),
    NamedListItem.configure({ onTurnItemIntoTask: lists?.onTurnItemIntoTask }),
    LineIdExtension,
    // Highlight (multicolour), text colour and their TextStyle mark (#279).
    ...formattingExtensions,
    ResizableImage.configure({
      inline: false,
      allowBase64: true,
      onError: handleImageError,
    }),
    DossierLink.configure({
      openOnClick: false,
      autolink: true,
      linkOnPaste: true,
      HTMLAttributes: {
        rel: 'noopener noreferrer',
        target: '_blank',
      },
    }),
    CodeBlockLowlight.configure({
      lowlight,
    }),
    DossierMention.configure({
      HTMLAttributes: {
        class: 'mention-node',
      },
      renderHTML({ node }) {
        const char = node.attrs.mentionSuggestionChar || '@';
        const label = node.attrs.label ?? node.attrs.id;
        const isTag = char === '#';

        const attrs: Record<string, string> = {
          'data-type': 'mention',
          'data-id': node.attrs.id,
          'data-label': label,
          'data-mention-suggestion-char': char,
          class: isTag ? 'mention-node mention-tag' : 'mention-node mention-person',
        };

        // For tags, store the color as a data attribute and inline style
        if (isTag && node.attrs.color) {
          attrs['data-color'] = node.attrs.color;
          attrs.style = `--tag-color: ${node.attrs.color}`;
        }

        // For follow-up mentions, store the task id
        if (node.attrs.followUpTaskId) {
          attrs['data-follow-up-task-id'] = node.attrs.followUpTaskId;
        }

        return ['span', attrs, `${char}${label}`];
      },
      renderText({ node }) {
        const char = node.attrs.mentionSuggestionChar || '@';
        return `${char}${node.attrs.label ?? node.attrs.id}`;
      },
      deleteTriggerWithBackspace: true,
      suggestions: [mentionSuggestion, tagSuggestion],
    }),
    TaskReference.configure({
      onTaskStatusChange,
    }),
    NoteReference,
    EntityLinkNode.configure({
      suggestion: entityLinkSuggestion,
    }),
    ImageUpload.configure({ onError: handleImageError }),
    AttachmentChipNode,
    AttachmentUpload.configure({ scratchpadId, onError: handleImageError }),
    Details.configure({
      persist: true,
      HTMLAttributes: {
        class: 'dossier-details',
      },
    }),
    DetailsContent,
    DetailsSummary,
    AutoSection,
    Indent,
    followUpExtension,
    ExcerptMark,
    ExcerptEmbed,
    FlagExtension.configure({ ...flags, enabled: flags?.enabled ?? false }),
  ];
}
